/**
 * Alchemy Gasless Transaction Hooks
 *
 * These hooks provide easy access to Alchemy's gasless transaction capabilities
 * using EIP-7702 for seamless smart account functionality.
 */

import {
  useAlchemySendTransaction,
  useAlchemyClient,
  useAlchemyPrepareSwap,
  useAlchemySubmitSwap,
} from "@account-kit/privy-integration";

/**
 * Hook to send gasless transactions
 *
 * @example
 * const { sendTransaction, isLoading, error } = useGaslessSendTransaction();
 * await sendTransaction({ to: '0x...', data: '0x...', value: '0x0' });
 *
 * @example Batch multiple transactions
 * await sendTransaction([
 *   { to: '0x...', data: '0x...' },
 *   { to: '0x...', data: '0x...' }
 * ]);
 */
export function useGaslessSendTransaction() {
  const { sendTransaction, isLoading, error } = useAlchemySendTransaction();

  return {
    sendTransaction,
    isLoading,
    error,
  };
}

/**
 * Hook to access the underlying Alchemy smart wallet client
 *
 * Use this for advanced operations that require direct client access
 *
 * @example
 * const { getClient } = useSmartWalletClient();
 * const { client, account } = await getClient();
 * console.log('Smart account address:', account.address);
 */
export function useSmartWalletClient() {
  const { getClient } = useAlchemyClient();

  return {
    getClient,
  };
}

/**
 * Hook to prepare and submit token swaps (gasless)
 *
 * @example
 * const { prepareSwap } = useTokenSwap();
 * const { submitSwap, isLoading } = useTokenSwap();
 *
 * const prepared = await prepareSwap({
 *   fromToken: '0xEee...', // ETH
 *   toToken: '0xA0b...', // USDC
 *   fromAmount: '1000000000000000000'
 * });
 * await submitSwap(prepared);
 */
export function useTokenSwap() {
  const { prepareSwap } = useAlchemyPrepareSwap();
  const { submitSwap, isLoading, error } = useAlchemySubmitSwap();

  return {
    prepareSwap,
    submitSwap,
    isLoading,
    error,
  };
}

/**
 * Re-export all Alchemy hooks for direct access if needed
 */
export {
  useAlchemySendTransaction,
  useAlchemyClient,
  useAlchemyPrepareSwap,
  useAlchemySubmitSwap,
};
